import { NewsDomainService } from './domain';
import { SentNewsFactory } from './entity/sentNewsFactory';

import type {
  SentNewsJsonPub,
  SentNewsJsonPvt,
} from './entity/sentNewsEntity';
import type { SentNewsIRepository } from './entity/sentNewsIRepository';
import type { EntityDataPubType } from '@/domain/entityBase';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { EntityDataBaseType } from '@/types/common/entityBase';

/**
 * お知らせアプリケーションサービス
 */
export class NewsApplicationService {
  private service!: NewsDomainService;

  private sentNewsFactory = new SentNewsFactory();

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param sentNewsRepository sentNewsRepository
   * @param logger logger
   */
  inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    sentNewsRepository: SentNewsIRepository,
    logger: Logger,
  ) {
    this.service = new NewsDomainService(logger);
    this.service.inject(
      wagakoNurseryApi,
      sentNewsRepository,
    );
  }

  /**
   * 送信済みお知らせ取得
   * @param token トークン
   * @returns リスト
   */
  async getSentNewsList(token: string | null) {
    return this.service.getSentNewsList(token);
  }

  /**
   * お知らせ既読
   * @param input 入力
   */
  async alreadyReadNews(
    input: EntityDataPubType<SentNewsJsonPvt, SentNewsJsonPub>,
  ) {
    const entity = this.sentNewsFactory.createFromJson(input);
    await this.service.alreadyReadNews(entity);
  }

  /**
   * コンテンツ読み込み
   * @param input 入力
   * @returns 結果
   */
  async loadContent(input: EntityDataPubType<SentNewsJsonPvt, SentNewsJsonPub>) {
    const entity = this.sentNewsFactory.createFromJson(input);
    const { content, info } = await this.service.loadContent(entity);
    return {
      /** コンテント */
      content: content.toJsonData(),
      /** インフォ */
      info: info.toJson(),
    };
  }

  /**
   * お知らせリストを更新する(Create)
   * @param sentNewsList ミルクリスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createSentNewsStore(
    sentNewsList: EntityDataBaseType<SentNewsJsonPvt, SentNewsJsonPub>[],
    payload: EntitySubscribeType,
    filter: ((json: SentNewsJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      sentNewsList,
      payload,
      this.sentNewsFactory,
      filter,
    );
  }

  /**
   * お知らせリストを更新する(Update)
   * @param sentNewsList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateSentNewsStore(
    sentNewsList: EntityDataBaseType<SentNewsJsonPvt, SentNewsJsonPub>[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      sentNewsList,
      payload,
      this.sentNewsFactory,
    );
  }

  /**
   * お知らせリストを更新する(Delete)
   * @param sentNewsList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteSentNewsStore(
    sentNewsList: EntityDataBaseType<SentNewsJsonPvt, SentNewsJsonPub>[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      sentNewsList,
      payload,
      this.sentNewsFactory,
    );
  }
}
